import { v6 as timeOrderedUUID } from 'uuid';
import { sqWorkbenchStore } from '@/core/core.stores';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { orFilters, valueFilter } from '@/serverSideTable/materializedTables/filters';
import { GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';
import { getTable } from '@/serverSideTable/materializedTables/queries';
import { sqItemsApi } from '@seeqdev/api/endpoint/ItemsApi';
import { sqMaterializedTablesApi } from '@seeqdev/api/endpoint/MaterializedTablesApi';
import type {
  ImpactReportColumnConfiguration,
  ImpactReportDashboardContent,
  ImpactReportFilterModel,
  ImpactReportLayout,
  MapZoomSettings,
} from '@/impactReports/impactReport.types';

const DEFAULT_LAYOUT_NAME = 'Default Layout';
const LAYOUT_TABLE_COLUMNS = [
  SeeqNames.MaterializedTables.ItemIdColumn,
  SeeqNames.MaterializedTables.DatumIdColumn,
  SeeqNames.ImpactReport.LayoutName,
  SeeqNames.ImpactReport.LayoutJson,
  SeeqNames.ImpactReport.LayoutColumnDefinitions,
  SeeqNames.ImpactReport.LayoutMapZoom,
  SeeqNames.ImpactReport.LayoutFilters,
  SeeqNames.ImpactReport.LayoutOwner,
  SeeqNames.ImpactReport.LayoutIsDefault,
];
let impactReportLayoutsTableId: string | undefined;

type JsonGuard<T> = (value: unknown) => value is T;

const isNonArrayObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const parseJson = <T>(raw: string | null | undefined, fallback: T, label: string, guard: JsonGuard<T>): T => {
  if (!raw) return fallback;

  try {
    const parsed: unknown = JSON.parse(raw);
    return guard(parsed) ? parsed : fallback;
  } catch (error) {
    console.warn(`Failed to parse Impact Report ${label} JSON`, error);
    return fallback;
  }
};
const isJsonArray = <T = unknown>(value: unknown): value is T[] => Array.isArray(value);

export function parseImpactReportLayoutJson(layoutJson: string | null | undefined) {
  return parseJson<ImpactReportDashboardContent[] | undefined>(layoutJson, undefined, 'layout', isJsonArray);
}

export function parseImpactReportColumnDefinitions(columnDefinitions: string | null | undefined) {
  return parseJson<ImpactReportColumnConfiguration | undefined>(
    columnDefinitions,
    undefined,
    'column definitions',
    isNonArrayObject as JsonGuard<ImpactReportColumnConfiguration | undefined>,
  );
}

export function parseImpactReportMapZoom(mapZoom: string | null | undefined) {
  return parseJson<MapZoomSettings | undefined>(
    mapZoom,
    undefined,
    'map zoom',
    isNonArrayObject as JsonGuard<MapZoomSettings | undefined>,
  );
}

export function parseImpactReportFilterModel(filterModel: string | null | undefined) {
  return parseJson<ImpactReportFilterModel | null>(
    filterModel,
    null,
    'filter model',
    isNonArrayObject as JsonGuard<ImpactReportFilterModel | null>,
  );
}

const serializeIfPresent = <T>(value: T | null | undefined): string | null =>
  value == null ? null : JSON.stringify(value);

export const resolveJsonField = <T>(
  value: T | null | undefined,
  existing: string | null | undefined,
  fallback: string | null,
) => {
  if (value === undefined) return existing ?? fallback;
  if (value === null) return null;
  return JSON.stringify(value);
};

export async function getImpactReportLayoutsTableId(): Promise<string | undefined> {
  if (impactReportLayoutsTableId) return impactReportLayoutsTableId;
  const { data } = await sqItemsApi.searchItems({
    filters: [`name==${SeeqNames.ImpactReport.LayoutTableName}`],
    types: ['TableDefinition'],
  });
  impactReportLayoutsTableId = data.items && data.items[0]?.id;
  return impactReportLayoutsTableId;
}

export async function requireImpactReportLayoutsTableId(): Promise<string> {
  const tableId = await getImpactReportLayoutsTableId();
  if (!tableId) {
    throw new Error('Impact Report layout table not found');
  }
  return tableId;
}

function mapLayoutRow(row: Record<string, any>): ImpactReportLayout | null {
  const layoutId = row[SeeqNames.MaterializedTables.DatumIdColumn];
  if (!layoutId) return null;
  const isDefaultValue = row[SeeqNames.ImpactReport.LayoutIsDefault];
  return {
    id: String(layoutId),
    name: (row[SeeqNames.ImpactReport.LayoutName] as string) ?? DEFAULT_LAYOUT_NAME,
    layoutJson: (row[SeeqNames.ImpactReport.LayoutJson] as string) ?? null,
    columnDefinitions: (row[SeeqNames.ImpactReport.LayoutColumnDefinitions] as string) ?? null,
    mapZoom: (row[SeeqNames.ImpactReport.LayoutMapZoom] as string) ?? null,
    filterModel: (row[SeeqNames.ImpactReport.LayoutFilters] as string) ?? null,
    ownerId: (row[SeeqNames.ImpactReport.LayoutOwner] as string) ?? null,
    isDefault: typeof isDefaultValue === 'boolean' ? isDefaultValue : isDefaultValue === 'true',
  };
}

export async function fetchImpactReportLayouts(): Promise<ImpactReportLayout[]> {
  const tableId = await getImpactReportLayoutsTableId();
  if (!tableId) return [];
  const currentUserId = sqWorkbenchStore.currentUser?.id;
  const defaultFilter = valueFilter(SeeqNames.ImpactReport.LayoutIsDefault, GraphQL.FilterTypes.Equals, true);
  const filter =
    currentUserId && currentUserId.length > 0
      ? orFilters(
          valueFilter(SeeqNames.ImpactReport.LayoutOwner, GraphQL.FilterTypes.Equals, currentUserId),
          defaultFilter,
        )
      : defaultFilter;

  const { data } = await getTable(tableId, {
    limit: 1000,
    columns: LAYOUT_TABLE_COLUMNS,
    filter,
  });
  return data.map(mapLayoutRow).filter(Boolean) as ImpactReportLayout[];
}

export async function publishLayoutRow({
  tableId,
  layoutId,
  name,
  layoutJson,
  columnDefinitions,
  mapZoom,
  filterModel,
  ownerId,
  isDefault,
}: {
  tableId: string;
  layoutId: string;
  name: string;
  layoutJson: string;
  columnDefinitions: string | null;
  mapZoom: string | null;
  filterModel: string | null;
  ownerId: string | null;
  isDefault: boolean;
}) {
  await sqMaterializedTablesApi.publishEvents(
    {
      events: [
        {
          genericEvent: {
            properties: [
              { name: SeeqNames.MaterializedTables.ItemIdColumn, value: tableId },
              { name: SeeqNames.MaterializedTables.DatumIdColumn, value: layoutId },
              { name: SeeqNames.ImpactReport.LayoutName, value: name },
              { name: SeeqNames.ImpactReport.LayoutJson, value: layoutJson },
              { name: SeeqNames.ImpactReport.LayoutColumnDefinitions, value: columnDefinitions },
              { name: SeeqNames.ImpactReport.LayoutMapZoom, value: mapZoom },
              { name: SeeqNames.ImpactReport.LayoutFilters, value: filterModel },
              { name: SeeqNames.ImpactReport.LayoutOwner, value: ownerId },
              { name: SeeqNames.ImpactReport.LayoutIsDefault, value: isDefault },
            ],
          },
        },
      ],
    },
    { tableId },
  );
}

export async function persistImpactReportLayout({
  tableId,
  layoutId,
  name,
  layoutJson,
  columnDefinitions,
  mapZoom,
  filterModel,
  ownerId,
  isDefault,
}: {
  tableId: string;
  layoutId: string;
  name: string;
  layoutJson: string;
  columnDefinitions: string | null;
  mapZoom: string | null;
  filterModel: string | null;
  ownerId: string | null;
  isDefault: boolean;
}): Promise<ImpactReportLayout> {
  await publishLayoutRow({
    tableId,
    layoutId,
    name,
    layoutJson,
    columnDefinitions,
    mapZoom,
    filterModel,
    ownerId,
    isDefault,
  });

  return {
    id: layoutId,
    name,
    layoutJson,
    columnDefinitions,
    mapZoom,
    filterModel,
    ownerId,
    isDefault,
  };
}

export async function createImpactReportLayout(
  name: string,
  layoutDefinition: ImpactReportDashboardContent[],
  columnConfiguration?: ImpactReportColumnConfiguration,
  mapZoom?: MapZoomSettings,
  filterModel?: ImpactReportFilterModel,
): Promise<ImpactReportLayout> {
  const trimmedName = name.trim();
  if (!trimmedName) {
    throw new Error('Layout name is required');
  }
  const tableId = await requireImpactReportLayoutsTableId();
  const layoutId = timeOrderedUUID();
  const ownerId = sqWorkbenchStore.currentUser?.id ?? null;
  const layoutJson = JSON.stringify(layoutDefinition);
  const columnDefinitions = serializeIfPresent(columnConfiguration);
  const mapZoomJson = serializeIfPresent(mapZoom);
  const filterModelJson = serializeIfPresent(filterModel);

  return persistImpactReportLayout({
    tableId,
    layoutId,
    name: trimmedName,
    layoutJson,
    columnDefinitions,
    mapZoom: mapZoomJson,
    filterModel: filterModelJson,
    ownerId,
    isDefault: false,
  });
}
